// Stazione di stampa: il monitor del magazzino resta in ascolto e stampa le
// etichette inviate dai telefoni. Con Chrome avviato con --kiosk-printing la
// stampa parte senza nemmeno il click sul dialog.
import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Printer, Radio, Download, Cable } from "lucide-react";
import { wmsApi } from "./wmsApi";
import { WButton, WInput, WField, WCard, WBadge } from "./WmsUi";

const agentFresh = (s) => s?.agent_last_seen && Date.now() - new Date(s.agent_last_seen).getTime() < 30000;

export default function WmsStation() {
  const [station, setStation] = useState(() => {
    try { return JSON.parse(localStorage.getItem("itrace_station") || "null"); } catch { return null; }
  });
  const [stations, setStations] = useState([]);
  const [name, setName] = useState("");
  const [log, setLog] = useState([]);
  const [agent, setAgent] = useState(null);
  const busyRef = useRef(false);
  const iframeRef = useRef(null);

  useEffect(() => { wmsApi.get("/wms/print-stations").then((r) => setStations(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (!station) return;
    const tick = async () => {
      if (busyRef.current) return;
      busyRef.current = true;
      try {
        const st = await wmsApi.get("/wms/print-stations");
        setStations(st.data);
        const mine = st.data.find((s) => s.id === station.id);
        const fresh = agentFresh(mine);
        setAgent(fresh ? mine : null);
        if (fresh) { busyRef.current = false; return; } // stampa il PC, non il browser
        const r = await wmsApi.get("/wms/print-jobs", { params: { station_id: station.id, status: "pending" } });
        for (const job of r.data) {
          const pdf = await wmsApi.get(`/wms/print-jobs/${job.id}.pdf`, { responseType: "blob" });
          const url = URL.createObjectURL(new Blob([pdf.data], { type: "application/pdf" }));
          await new Promise((resolve) => {
            const f = iframeRef.current;
            f.onload = () => {
              try { f.contentWindow.print(); } catch { window.open(url, "_blank"); }
              setTimeout(resolve, 1200);
            };
            f.src = url;
          });
          await wmsApi.post(`/wms/print-jobs/${job.id}/done`);
          setLog((l) => [{ ts: new Date().toLocaleTimeString("it-IT"), n: job.piece_ids.length, by: job.created_by }, ...l].slice(0, 30));
        }
      } catch { /* riprova al prossimo giro */ }
      busyRef.current = false;
    };
    tick();
    const int = setInterval(tick, 4000);
    return () => clearInterval(int);
  }, [station]);

  const createStation = async () => {
    if (!name.trim()) return;
    try {
      const r = await wmsApi.post("/wms/print-stations", { name });
      localStorage.setItem("itrace_station", JSON.stringify({ id: r.data.id, name: r.data.name }));
      setStation({ id: r.data.id, name: r.data.name });
    } catch { toast.error("Errore creazione stazione"); }
  };

  const adopt = (s) => {
    localStorage.setItem("itrace_station", JSON.stringify({ id: s.id, name: s.name }));
    setStation({ id: s.id, name: s.name });
  };

  const downloadAgent = async () => {
    try {
      const r = await wmsApi.get(`/wms/print-stations/${station.id}/agent.ps1`, {
        params: { base_url: process.env.REACT_APP_BACKEND_URL },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "AgenteStampa-iTRACE.ps1"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore download agente"); }
  };

  if (!station) {
    return (
      <div className="max-w-xl mx-auto space-y-5">
        <h1 className="font-['Chivo'] font-black text-3xl tracking-tighter uppercase">Stazione di stampa</h1>
        <p className="text-zinc-500">Questo dispositivo diventerà una postazione: le etichette inviate dai telefoni usciranno da qui.</p>
        {stations.length > 0 && (
          <WCard className="p-4 space-y-2 border-2 border-zinc-300">
            <p className="text-xs uppercase font-bold text-zinc-500">Postazioni esistenti</p>
            {stations.map((s) => (
              <WButton key={s.id} className="w-full" onClick={() => adopt(s)} data-testid={`station-adopt-${s.id}`}>Usa "{s.name}"</WButton>
            ))}
          </WCard>
        )}
        <WField label="Oppure crea una nuova postazione">
          <WInput value={name} onChange={(e) => setName(e.target.value)} placeholder="es. Cella 1 · Banco carico" data-testid="station-name-input" />
        </WField>
        <WButton variant="primary" className="w-full" onClick={createStation} data-testid="station-create-btn">Attiva stazione</WButton>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-zinc-950 text-white p-8 flex flex-col items-center gap-3 border-4 border-[#FF4500]" data-testid="station-listening">
        <Radio className="w-14 h-14 text-[#FF4500] animate-pulse" />
        <p className="font-['Chivo'] font-black text-3xl uppercase tracking-tight text-center">{station.name}</p>
        {agent ? (
          <div className="flex flex-col items-center gap-1" data-testid="station-agent-active">
            <WBadge tone="green"><Cable className="w-3 h-3 mr-1" /> Agente PC collegato</WBadge>
            <p className="text-zinc-400 text-sm">Stampa diretta su <span className="text-white font-bold">{agent.agent_printer || "stampante TSC"}</span> — questo browser non stampa</p>
          </div>
        ) : (
          <p className="text-zinc-400 font-bold uppercase tracking-[0.25em] text-sm">In ascolto — le etichette escono da qui</p>
        )}
        <button className="text-xs text-zinc-500 underline mt-2" onClick={() => { localStorage.removeItem("itrace_station"); setStation(null); }} data-testid="station-release">cambia postazione</button>
      </div>
      {!agent && <p className="text-xs text-zinc-400 text-center">Suggerimento: avvia Chrome con <span className="font-mono bg-zinc-100 px-1">--kiosk-printing</span> e la stampa parte senza click sul dialog.</p>}
      <WCard className="max-w-xl mx-auto p-4 space-y-3 border-2 border-zinc-300" data-testid="station-agent-card">
        <div className="flex items-center gap-2">
          <Cable className="w-5 h-5 text-[#FF4500]" />
          <p className="font-['Chivo'] font-black uppercase">Stampa diretta TSC — senza PDF</p>
        </div>
        <p className="text-sm text-zinc-500">Sul PC Windows collegato alla TSC MB241T: scarica l'agente, tasto destro → <b>Esegui con PowerShell</b> e lascia la finestra aperta. Trova da solo la stampante TSC e stampa le etichette in automatico, senza aprire nessun PDF.</p>
        <WButton variant="dark" className="w-full" onClick={downloadAgent} data-testid="station-agent-download">
          <Download className="w-5 h-5" /> Scarica agente di stampa (Windows)
        </WButton>
      </WCard>
      <div className="max-w-xl mx-auto space-y-1">
        <p className="text-xs uppercase font-bold text-zinc-500">Etichette stampate</p>
        {log.length === 0 && <p className="text-sm text-zinc-400" data-testid="station-log-empty">Nessuna stampa ancora — in attesa dei telefoni…</p>}
        {log.map((l, i) => (
          <div key={i} className="flex items-center gap-3 bg-white border border-zinc-200 px-3 h-11 text-sm" data-testid="station-log-row">
            <Printer className="w-4 h-4 text-[#FF4500]" />
            <span className="font-mono text-zinc-400">{l.ts}</span>
            <span className="font-bold">{l.n} etichetta{l.n > 1 ? "e" : ""}</span>
            <span className="text-zinc-500">da {l.by || "operatore"}</span>
          </div>
        ))}
      </div>
      <iframe ref={iframeRef} title="print" className="hidden" />
    </div>
  );
}
